import { useQuery } from "@apollo/client";
import { useCallback, useMemo, useRef } from "react";
import { useAppointmentTransform } from "@/domains/appointment/appointments/shared/utils/useAppointmentTransform";
import type { Appointment } from "@/domains/appointment/appointments/types/appointment";
import { WAITING_TO_CONFIRM_APPOINTMENTS_QUERY } from "@/graphql/appointments";
import { useSalonStore } from "@/lib/store/salon";
import { timezoneUtils } from "@/lib/timezone";

export interface UseConfirmationsAppointmentsReturn {
  appointments: Appointment[];
  loading: boolean;
  loadingMore: boolean;
  error: any;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  byDate: Record<string, Appointment[]>;
}

/**
 * Hook to fetch all appointments with bookingStatus: "PENDING" (waiting for confirmation)
 * Groups them by date for display in confirmations view
 */
export const useConfirmationsAppointments = (): UseConfirmationsAppointmentsReturn => {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const isLoadingMoreRef = useRef(false);
  const _lastEdgesLengthRef = useRef(0);

  // TODO: Fix pagination instead of fetching a large number of appointments.
  const { data, loading, error, fetchMore, refetch } = useQuery(
    WAITING_TO_CONFIRM_APPOINTMENTS_QUERY,
    {
      variables: {
        salonId: currentSalon?.id ?? "",
        filters: {
          first: 1000, // HACK: Increased limit to avoid pagination issues.
          sortOrder: "asc",
        },
      },
      skip: !currentSalon?.id,
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    }
  );

  // Transform GraphQL data to appointment objects
  const appointments = useAppointmentTransform(
    data?.waitingToConfirmAppointments?.edges?.map((edge: any) => edge.node) || []
  );

  // Group appointments by date
  const byDate = useMemo(() => {
    return appointments.reduce<Record<string, Appointment[]>>((acc, apt) => {
      const startTime = timezoneUtils.fromISO(apt.startTime);
      const key = startTime.toFormat("yyyy-MM-dd");
      acc[key] ??= [];
      acc[key].push(apt);
      return acc;
    }, {});
  }, [appointments]);

  // Stable loading state - only show loading for initial load
  const isInitialLoading = loading && appointments.length === 0;

  // Pagination handlers
  const hasMore = data?.waitingToConfirmAppointments?.pageInfo?.hasNextPage || false;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMoreRef.current) return;

    const edges = data?.waitingToConfirmAppointments?.edges;
    const lastCursor = edges?.slice(-1)?.[0]?.cursor;

    if (lastCursor) {
      isLoadingMoreRef.current = true;
      fetchMore({
        variables: {
          salonId: currentSalon?.id ?? "",
          after: lastCursor,
          filters: {
            first: 50, // Use same page size for subsequent loads
            sortOrder: "asc",
          },
        },
      }).finally(() => {
        isLoadingMoreRef.current = false;
      });
    }
  }, [hasMore, fetchMore, currentSalon?.id, data?.waitingToConfirmAppointments?.edges]);

  return {
    appointments,
    loading: isInitialLoading, // Only show loading for initial load when no data exists
    loadingMore: false, // Don't show loading indicator for pagination to prevent flashing
    error,
    hasMore,
    loadMore,
    refetch,
    byDate,
  };
};
